import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Edit, User, Building2, Briefcase } from 'lucide-react';
import api from '../../services/api';

const ViewUser = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);

    useEffect(() => {
        fetchUserDetails();
    }, [id]);

    const fetchUserDetails = async () => {
        try {
            const userData = await api.getUser(id);
            setUser(userData);
            setLoading(false);
        } catch (error) {
            console.error('Failed to fetch user details:', error);
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const getRoleLabel = (roleKey) => {
        const roles = {
            'admin': 'Administrator',
            'oem': 'OEM Partner',
            'supervisor': 'SuperAligner',
            'installer': 'Aligner',
            'junioraligner': 'Junior Aligner'
        };
        return roles[roleKey] || roleKey;
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading user details...</p>
                </div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="text-center">
                    <p className="text-red-600">User not found</p>
                    <Button onClick={() => navigate('/admin/settings/users')} className="mt-4">
                        Back to Users
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
            <div className="max-w-5xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/admin/settings/users')}
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">User Details</h1>
                            <p className="text-sm text-gray-600 mt-1">View complete user information</p>
                        </div>
                    </div>
                    <Button
                        onClick={() => navigate(`/admin/settings/users/edit/${id}`)}
                        className="flex items-center gap-2"
                    >
                        <Edit className="h-4 w-4" />
                        Edit User
                    </Button>
                </div>

                {/* Photo & Basic Info Card */}
                <Card className="mb-6">
                    <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row gap-6 items-start">
                            {/* Photo Section */}
                            <div className="flex-shrink-0">
                                {user.employee?.faceReferenceUrl ? (
                                    <div className="relative">
                                        <img
                                            src={user.employee.faceReferenceUrl}
                                            alt={user.name}
                                            className="w-48 h-48 object-cover rounded-lg border-4 border-blue-100 shadow-lg"
                                        />
                                        {user.employee.faceRecognitionEnabled && (
                                            <div className="absolute bottom-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                                                <User className="h-3 w-3" />
                                                Face Recognition
                                            </div>
                                        )}
                                    </div>
                                ) : user.oem?.logoUrl ? (
                                    <img
                                        src={user.oem.logoUrl}
                                        alt={user.oem.companyName}
                                        className="w-48 h-48 object-contain rounded-lg border-4 border-blue-100 shadow-lg bg-white p-4"
                                    />
                                ) : (
                                    <div className="w-48 h-48 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center border-4 border-blue-300 shadow-lg">
                                        <User className="h-24 w-24 text-blue-600" />
                                    </div>
                                )}
                            </div>

                            {/* Basic Info */}
                            <div className="flex-1 space-y-4">
                                <div>
                                    <h2 className="text-3xl font-bold text-gray-900">{user.name}</h2>
                                    <p className="text-lg text-gray-600 mt-1">{getRoleLabel(user.role)}</p>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t">
                                    <div>
                                        <p className="text-sm text-gray-500">Email</p>
                                        <p className="text-base font-medium text-gray-900">{user.email || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Phone</p>
                                        <p className="text-base font-medium text-gray-900">{user.phone || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Status</p>
                                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                            {user.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Joined On</p>
                                        <p className="text-base font-medium text-gray-900">{formatDate(user.createdAt)}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* OEM Details */}
                {user.oem && (
                    <Card className="mb-6">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Building2 className="h-5 w-5 text-blue-600" />
                                Company Details
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div>
                                    <p className="text-sm text-gray-500">Company Name</p>
                                    <p className="text-base font-medium text-gray-900">{user.oem.companyName || '-'}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">GST Number</p>
                                    <p className="text-base font-medium text-gray-900">{user.oem.gstNumber || '-'}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">PAN Number</p>
                                    <p className="text-base font-medium text-gray-900">{user.oem.panNumber || '-'}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Contact Person</p>
                                    <p className="text-base font-medium text-gray-900">{user.oem.contactPerson || '-'}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Contact Person Mobile</p>
                                    <p className="text-base font-medium text-gray-900">{user.oem.contactPersonMobile || '-'}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Contact Person Email</p>
                                    <p className="text-base font-medium text-gray-900">{user.oem.contactPersonEmail || '-'}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Contact Person Designation</p>
                                    <p className="text-base font-medium text-gray-900">{user.oem.contactPersonDesignation || '-'}</p>
                                </div>
                                <div className="sm:col-span-2">
                                    <p className="text-sm text-gray-500">Address</p>
                                    <p className="text-base font-medium text-gray-900">{user.oem.address || '-'}</p>
                                </div>
                                <div className="sm:col-span-2">
                                    <p className="text-sm text-gray-500">Billing Address</p>
                                    <p className="text-base font-medium text-gray-900">{user.oem.billingAddress || '-'}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Employee Details */}
                {user.employee && (
                    <>
                        {/* Professional Info */}
                        <Card className="mb-6">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Briefcase className="h-5 w-5 text-blue-600" />
                                    Professional Information
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                    <div>
                                        <p className="text-sm text-gray-500">Employee Code</p>
                                        <p className="text-base font-medium text-gray-900">{user.employee.employeeCode || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Designation</p>
                                        <p className="text-base font-medium text-gray-900">{user.employee.designation || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Department</p>
                                        <p className="text-base font-medium text-gray-900">{user.employee.department || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Joining Date</p>
                                        <p className="text-base font-medium text-gray-900">{formatDate(user.employee.joiningDate)}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Employment Type</p>
                                        <p className="text-base font-medium text-gray-900">{user.employee.employmentType?.replace('_', ' ').toUpperCase() || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Skill Level</p>
                                        <p className="text-base font-medium text-gray-900">{user.employee.skillLevel?.toUpperCase() || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Years of Experience</p>
                                        <p className="text-base font-medium text-gray-900">{user.employee.yearsOfExperience || '0'} years</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Blood Group</p>
                                        <p className="text-base font-medium text-gray-900">{user.employee.bloodGroup || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Specialization</p>
                                        <p className="text-base font-medium text-gray-900">{user.employee.specialization || '-'}</p>
                                    </div>
                                    {user.employee.certifications && (
                                        <div className="sm:col-span-2 lg:col-span-3">
                                            <p className="text-sm text-gray-500">Certifications</p>
                                            <p className="text-base font-medium text-gray-900">{user.employee.certifications}</p>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Bank & Payment Details */}
                        <Card className="mb-6">
                            <CardHeader>
                                <CardTitle>Bank & Payment Details</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    <div>
                                        <p className="text-sm text-gray-500">Monthly Salary</p>
                                        <p className="text-base font-medium text-gray-900">
                                            {user.employee.monthlySalary ? `₹ ${Number(user.employee.monthlySalary).toLocaleString('en-IN')}` : '-'}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Payment Mode</p>
                                        <p className="text-base font-medium text-gray-900">{user.employee.paymentMode?.toUpperCase() || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Bank Name</p>
                                        <p className="text-base font-medium text-gray-900">{user.employee.bankName || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Bank Branch</p>
                                        <p className="text-base font-medium text-gray-900">{user.employee.bankBranch || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Account Number</p>
                                        <p className="text-base font-medium text-gray-900">{user.employee.accountNumber || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">IFSC Code</p>
                                        <p className="text-base font-medium text-gray-900">{user.employee.ifscCode || '-'}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Personal Documents */}
                        <Card className="mb-6">
                            <CardHeader>
                                <CardTitle>Personal Documents</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    <div>
                                        <p className="text-sm text-gray-500">PAN Number</p>
                                        <p className="text-base font-medium text-gray-900">{user.employee.panNumber || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Aadhar Number</p>
                                        <p className="text-base font-medium text-gray-900">{user.employee.aadharNumber || '-'}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Address Details */}
                        <Card className="mb-6">
                            <CardHeader>
                                <CardTitle>Address Details</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    <div>
                                        <p className="text-sm text-gray-500">Current Address</p>
                                        <p className="text-base font-medium text-gray-900">{user.employee.currentAddress || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Permanent Address</p>
                                        <p className="text-base font-medium text-gray-900">{user.employee.permanentAddress || '-'}</p>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                        <div>
                                            <p className="text-sm text-gray-500">City</p>
                                            <p className="text-base font-medium text-gray-900">{user.employee.city || '-'}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">State</p>
                                            <p className="text-base font-medium text-gray-900">{user.employee.state || '-'}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">Pin Code</p>
                                            <p className="text-base font-medium text-gray-900">{user.employee.pinCode || '-'}</p>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Emergency Contact */}
                        <Card className="mb-6">
                            <CardHeader>
                                <CardTitle>Emergency Contact</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                                    <div>
                                        <p className="text-sm text-gray-500">Contact Name</p>
                                        <p className="text-base font-medium text-gray-900">{user.employee.emergencyContactName || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Relation</p>
                                        <p className="text-base font-medium text-gray-900">{user.employee.emergencyContactRelation || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Phone</p>
                                        <p className="text-base font-medium text-gray-900">{user.employee.emergencyContactPhone || '-'}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </>
                )}
            </div>
        </div>
    );
};

export default ViewUser;
